import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Stack,
  Tooltip,
  useColorMode,
} from "@chakra-ui/react";
import { BsStar, BsStarFill } from "react-icons/bs";
import {
  getStudyNote,
  saveStudyNote,
  getWatchlist,
  addToWatchlist,
  removeFromWatchlist,
} from "../store/study/thunks";
import {
  selectStudyNoteFor,
  selectWatchlist,
  selectIsOnWatchlist,
} from "../store/study/selectors";
import useToastNotification from "../hooks/useToastNotification";
import useAsyncState from "../hooks/useAsyncState";
import Watchlist from "./study/Watchlist";
import StudyChart from "./study/StudyChart";
import StudyNotes from "./study/StudyNotes";
import TickerSearch from "./study/TickerSearch";

const TICKER_RE = /^[A-Za-z0-9.\-]{1,10}$/;

const normalizeTicker = (raw) => {
  if (!raw) return null;
  const upper = String(raw).trim().toUpperCase();
  return TICKER_RE.test(upper) ? upper : null;
};

export default function Study() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { colorMode } = useColorMode();
  const { ticker: tickerParam } = useParams();
  const showToast = useToastNotification();

  const initialTicker = useMemo(() => normalizeTicker(tickerParam), [tickerParam]);
  const [activeTicker, setActiveTicker] = useState(initialTicker);
  // Local in-flight markers — the watchlist + note mutation thunks are
  // excluded from the global ui.loading matcher, so we track per-action
  // progress here for the local Save / Add / Remove buttons.
  const [addingTicker, setAddingTicker] = useState(null);
  const [removingTicker, setRemovingTicker] = useState(null);
  const [savingNote, setSavingNote] = useState(false);

  const watchlist = useSelector(selectWatchlist);
  const watchlistLoaded = useSelector((s) => Boolean(s.study?.watchlistLoaded));
  const note = useSelector(selectStudyNoteFor(activeTicker));
  const isOnWatchlist = useSelector(selectIsOnWatchlist(activeTicker));

  useAsyncState({
    onError: (info) =>
      showToast(info?.response?.data?.result || "Something went wrong", {
        status: "error",
      }),
  });

  // Initial load: hydrate watchlist + the deep-linked ticker's note.
  // Loaded flag prevents a remount-driven refetch loop when the page is
  // re-entered after Home's AnimatePresence cycles ui.loading.
  useEffect(() => {
    if (!watchlistLoaded) {
      dispatch(getWatchlist());
    }
  }, [dispatch, watchlistLoaded]);

  useEffect(() => {
    if (activeTicker) {
      dispatch(getStudyNote({ ticker_name: activeTicker }));
    }
  }, [dispatch, activeTicker]);

  // Keep URL and active ticker in sync. Coming in via deep link populates the
  // state; clicking around the watchlist updates the URL through navigate().
  useEffect(() => {
    const next = normalizeTicker(tickerParam);
    if (next && next !== activeTicker) {
      setActiveTicker(next);
    }
  }, [tickerParam, activeTicker]);

  const handleSelectTicker = useCallback(
    (raw) => {
      const ticker = normalizeTicker(raw);
      if (!ticker) return;
      setActiveTicker(ticker);
      navigate(`/study/${ticker}`, { replace: false });
    },
    [navigate]
  );

  const handleAddToWatchlist = useCallback(
    async (raw) => {
      const ticker = normalizeTicker(raw);
      if (!ticker) {
        showToast("Invalid ticker symbol", { status: "error" });
        return;
      }
      setAddingTicker(ticker);
      try {
        const result = await dispatch(addToWatchlist({ ticker_name: ticker }));
        if (addToWatchlist.fulfilled.match(result)) {
          const msg = result.payload?.result || "Added to watchlist";
          showToast(msg);
          if (!activeTicker) handleSelectTicker(ticker);
        }
      } finally {
        setAddingTicker(null);
      }
    },
    [dispatch, showToast, activeTicker, handleSelectTicker]
  );

  const handleRemoveFromWatchlist = useCallback(
    async (raw) => {
      const ticker = normalizeTicker(raw);
      if (!ticker) return;
      setRemovingTicker(ticker);
      try {
        const result = await dispatch(removeFromWatchlist({ ticker_name: ticker }));
        if (removeFromWatchlist.fulfilled.match(result)) {
          showToast("Removed from watchlist");
        }
      } finally {
        setRemovingTicker(null);
      }
    },
    [dispatch, showToast]
  );

  const handleSaveNote = useCallback(
    async (ticker, content) => {
      const target = normalizeTicker(ticker);
      if (!target) return;
      setSavingNote(true);
      try {
        const result = await dispatch(saveStudyNote({ ticker_name: target, content }));
        if (saveStudyNote.fulfilled.match(result)) {
          showToast("Note saved");
        }
      } finally {
        setSavingNote(false);
      }
    },
    [dispatch, showToast]
  );

  const toggleWatchlist = useCallback(() => {
    if (!activeTicker) return;
    if (isOnWatchlist) {
      handleRemoveFromWatchlist(activeTicker);
    } else {
      handleAddToWatchlist(activeTicker);
    }
  }, [activeTicker, isOnWatchlist, handleAddToWatchlist, handleRemoveFromWatchlist]);

  return (
    <Flex
      flexDirection="column"
      minH="calc(100vh - 60px)"
      bg={colorMode === "light" ? "gray.200" : "gray.800"}
    >
      <Box flex="auto" p={4}>
        <Stack
          direction={{ base: "column", md: "row" }}
          align={{ base: "stretch", md: "center" }}
          justify="space-between"
          spacing={3}
          mb={3}
        >
          <HStack spacing={3} flexShrink={0}>
            <Heading size="md">
              Study {activeTicker ? `— ${activeTicker}` : ""}
            </Heading>
            {activeTicker && (
              isOnWatchlist ? (
                <Tooltip label="Remove from watchlist" hasArrow fontSize="xs">
                  <IconButton
                    size="sm"
                    aria-label="Remove from watchlist"
                    icon={<Icon as={BsStarFill} />}
                    colorScheme="yellow"
                    variant="solid"
                    onClick={toggleWatchlist}
                  />
                </Tooltip>
              ) : (
                <Button
                  size="sm"
                  leftIcon={<Icon as={BsStar} />}
                  variant="outline"
                  colorScheme="yellow"
                  onClick={toggleWatchlist}
                >
                  Add to watchlist
                </Button>
              )
            )}
          </HStack>
          <Box w={{ base: "100%", md: "220px" }}>
            <TickerSearch
              onSubmit={handleSelectTicker}
              placeholder={activeTicker ? "Change ticker" : "Search ticker"}
              submitLabel={activeTicker ? "Change" : "Open"}
              submitColorScheme="blue"
            />
          </Box>
        </Stack>

        <Stack
          direction={{ base: "column", lg: "row" }}
          spacing={4}
          align="stretch"
          h={{ base: "auto", lg: "calc(100vh - 180px)" }}
        >
          <Watchlist
            watchlist={watchlist}
            selectedTicker={activeTicker}
            onSelectTicker={handleSelectTicker}
            onAddTicker={handleAddToWatchlist}
            onRemoveTicker={handleRemoveFromWatchlist}
            addingTicker={addingTicker}
            removingTicker={removingTicker}
          />
          <Box
            flex={{ base: "0 0 auto", lg: "1" }}
            h={{ base: "65vh", lg: "auto" }}
            minW={0}
          >
            <StudyChart ticker={activeTicker} />
          </Box>
          <StudyNotes
            ticker={activeTicker}
            note={note}
            onSave={handleSaveNote}
            isSaving={savingNote}
          />
        </Stack>
      </Box>
    </Flex>
  );
}
